/**
 * Squish Deli plate builder: <sd-plate-builder> in sections/sd-plate-builder.liquid (BUILD A PLATE role).
 *
 * State { size: 3|5|8, seats: [{h: handle, v: variantId}], mini: boolean } is saved in localStorage
 * ("sd-plate:v1", per-viewer convenience only). Seats fill in order; removing one closes the gap.
 * Plate rules (BUILD-SPEC 4): weight cap (metafield squish.weight_est_g), max butters, max big squishes. They are
 * explained as per-plate limits, never as a one-box promise (bible: no one-parcel claim before G2).
 * The plate starts EMPTY. ?prefill= puts one piece in seat 1, ?seats= loads a whole plate; nothing is ever pre-seeded.
 * Every picture of a seat (plate, tray, sheet, chips) is the chosen variant's own image, falling back to the product.
 * "Add platter to bag" sends every seat in ONE /cart/add.js call through SquishDeli.addItems (assets/sd-ui.js),
 * which fires Horizon's CartLinesUpdateEvent, so the drawer, bubble and cart extras re-render the Horizon way.
 */
import { addItems, formatMoney, nyToday, squish } from '@theme/sd-ui';

const STORE_KEY = 'sd-plate:v1';
const MAX_AGE = 1000 * 60 * 60 * 24 * 30;
const SIZES = [3, 5, 8];
const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function readStore() {
  try {
    const raw = window.localStorage.getItem(STORE_KEY);
    if (!raw) return null;
    const value = JSON.parse(raw);
    if (!value || typeof value !== 'object' || Date.now() - (value.t || 0) > MAX_AGE) return null;
    return value;
  } catch {
    return null;
  }
}

function writeStore(state) {
  try {
    window.localStorage.setItem(
      STORE_KEY,
      JSON.stringify({ size: state.size, seats: state.seats, mini: state.mini, t: Date.now() })
    );
  } catch {
    /* private mode or blocked storage: the builder still works, it just forgets */
  }
}

function clearStore() {
  try {
    window.localStorage.removeItem(STORE_KEY);
  } catch {
    /* ignore */
  }
}

/** Today in New York as YYYY-MM-DD (honours ?sd_date= through sd-ui). */
const todayISO = () => nyToday().toISOString().slice(0, 10);

/** BUILD-SPEC 5 Christmas phases (same dates as snippets/sd-xmas-line.liquid). */
export function xmasPhase(t = todayISO()) {
  if (t <= '2026-11-22') return 'pre';
  if (t <= '2026-11-24') return 'risk';
  if (t <= '2026-12-24') return 'late';
  return 'none';
}

/** Show the right [data-sd-xmas] line inside root; hide the wrapper when there is none. */
export function applyXmas(root) {
  const phase = xmasPhase();
  for (const wrap of root.querySelectorAll('[data-sd-xmas-wrap]')) {
    wrap.hidden = phase === 'none';
    for (const line of wrap.querySelectorAll('[data-sd-xmas]')) line.hidden = line.getAttribute('data-sd-xmas') !== phase;
  }
}

const plural = (n, one, many = `${one}s`) => (n === 1 ? one : many);
const pick = (list) => list[Math.floor(Math.random() * list.length)];
const esc = (s) =>
  String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
/** Warm the browser cache so a seat, chip or tray thumb never shows up empty. */
const warmed = new Set();
const preload = (url) => {
  if (!url || warmed.has(url)) return;
  warmed.add(url);
  const img = new Image();
  img.decoding = 'async';
  img.src = url;
};
/** Fade an <img> in once it has pixels (the placeholder underneath shows until then). */
const reveal = (img) => {
  if (!(img instanceof HTMLImageElement)) return;
  const done = () => img.classList.add('is-loaded');
  if (img.complete && img.naturalWidth) done();
  else img.addEventListener('load', done, { once: true });
};
const BLANK = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';

/** The builder data carries 200 px square crops; the sheet's big picture asks Shopify for a sharp 900 px one. */
const bigImg = (src) =>
  src ? src.replace(/([?&])width=\d+/, '$1width=900').replace(/&height=\d+/, '').replace(/&crop=[a-z]+/, '') : '';

class SdPlateBuilder extends HTMLElement {
  /** @type {AbortController | undefined} */ #ac;
  /** @type {IntersectionObserver | undefined} */ #io;
  #data;
  /** @type {Map<string, any>} */ #byHandle = new Map();
  #state = { size: 8, seats: [], mini: false };
  #undo = null;
  #busy = false;
  #toastTimer = 0;
  #sheetCtx = null;
  /** Label for the toast's action button when #undo holds an alternative state rather than the previous one. */
  #undoLabel = 'Undo';
  #undoSay = 'Undone.';
  /** First-render announcement (prefill / seats= link). */
  #hello = null;
  /** Arrival from a product page (?prefill=): { h, kept, picked } drives the top banner; null hides it. */
  #pre = null;

  connectedCallback() {
    const json = this.querySelector('script[data-sd-pb-data]');
    if (!json) return;
    try {
      this.#data = JSON.parse(json.textContent || '{}');
    } catch (error) {
      console.warn('[sd-builder] bad data', error);
      return;
    }
    this.#ac = new AbortController();
    this.#byHandle = new Map((this.#data.products || []).map((p) => [p.h, p]));
    this.#state = this.#initialState();
    for (const seat of this.#state.seats) preload(this.#imgFor(seat));
    this.#wire();
    applyXmas(this);
    this.#render();
    this.#watchTicket();
    this.classList.add('is-ready');
    if (this.#hello) this.#say(this.#hello.message, { undo: this.#hello.undo });
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#io?.disconnect();
    clearTimeout(this.#toastTimer);
  }

  /* ------------------------------------------------------------------ */
  /* Data helpers                                                        */
  /* ------------------------------------------------------------------ */

  get #size() {
    return this.#data.sizes.find((s) => s.n === this.#state.size) || this.#data.sizes[this.#data.sizes.length - 1];
  }

  #noun(n = this.#state.size) {
    return n >= 8 ? 'platter' : 'plate';
  }

  #variant(p, id) {
    return p.vars.find((v) => String(v.id) === String(id));
  }

  /** The style name worth saying out loud ('' for one-style products or "Mood Mango / Mood Mango"). */
  #style(p, id) {
    const v = this.#variant(p, id);
    return p.vars.length > 1 && v && v.t !== p.t ? v.t : '';
  }

  #firstAvailable(p) {
    return p.vars.find((v) => v.a);
  }

  /** The picture of exactly this style (one piece), or the product picture when the style has none. */
  #imgOf(p, id) {
    return (id != null && this.#variant(p, id)?.img) || p.img || '';
  }

  #imgFor(seat) {
    const p = seat && this.#byHandle.get(seat.h);
    return p ? this.#imgOf(p, seat.v) : '';
  }

  #offerLive() {
    return Boolean(this.#data.offer && this.#data.mini && (!this.#data.offerEnd || todayISO() <= this.#data.offerEnd));
  }

  #miniOn() {
    return this.#state.mini && this.#state.size === 8 && this.#offerLive();
  }

  /**
   * Why a product can't take the next seat, or null when it can.
   * @returns {null | 'soldout' | 'full' | 'butter' | 'big' | 'weight'}
   */
  #why(p, seats = this.#state.seats, size = this.#state.size) {
    if (!p.vars.some((v) => v.a)) return 'soldout';
    if (seats.length >= size) return 'full';
    const on = seats.map((s) => this.#byHandle.get(s.h)).filter(Boolean);
    if (p.butter && on.filter((x) => x.butter).length >= this.#data.maxButter) return 'butter';
    if (p.big && on.filter((x) => x.big).length >= this.#data.maxBig) return 'big';
    const grams = on.reduce((sum, x) => sum + (Number(x.w) || 0), 0);
    if (grams + (Number(p.w) || 0) > this.#data.cap) return 'weight';
    return null;
  }

  #whyText(why) {
    const { maxBig, maxButter, cap } = this.#data;
    const kg = (cap / 1000).toLocaleString('en-US', { maximumFractionDigits: 1 });
    const words = ['zero', 'one', 'two', 'three', 'four', 'five', 'six'];
    const big = words[maxBig] || String(maxBig);
    switch (why) {
      case 'full':
        return `Your ${this.#size.name} is full. Tap a seat to swap a piece, or pick a bigger plate.`;
      case 'butter':
        return `This plate is at the weight limit: one parcel can only carry so much butter. ${
          (words[maxButter] || String(maxButter)).replace(/^./, (c) => c.toUpperCase())
        } ${plural(maxButter, 'butter')} per plate.`;
      case 'big':
        return `${big.charAt(0).toUpperCase()}${big.slice(1)} heavy pieces per plate, max (marked Heavy or Jumbo), and each plate has a weight cap.`;
      case 'weight':
        return `That one would take this plate over its weight cap (about ${kg} kg). Try a lighter piece.`;
      case 'soldout':
        return 'That one is sold out right now.';
      default:
        return '';
    }
  }

  #whyShort(why) {
    return { full: 'Plate full', butter: 'Butter limit', big: 'Heavy limit', weight: 'Too heavy', soldout: 'Sold out' }[why] || 'Add';
  }

  /** Keep seats in order while they pass every rule (used after restore, prefill and size changes). */
  #validSeats(seats, size) {
    const kept = [];
    for (const seat of seats) {
      const p = this.#byHandle.get(seat.h);
      if (!p) continue;
      let v = this.#variant(p, seat.v);
      if (!v || !v.a) v = this.#firstAvailable(p);
      if (!v) continue;
      if (this.#why(p, kept, size)) continue;
      kept.push({ h: p.h, v: v.id });
    }
    return kept;
  }

  #initialState() {
    const params = new URLSearchParams(window.location.search);
    const saved = readStore();
    let size = SIZES.includes(Number(saved?.size)) ? Number(saved.size) : Number(this.#data.defaultSize) || 8;
    const askedSize = Number(params.get('size'));
    if (SIZES.includes(askedSize)) size = askedSize;

    // No seed: a first visit starts with an empty plate, so nothing lands in the bag that the shopper didn't pick.
    let seats = Array.isArray(saved?.seats) ? saved.seats : [];

    // ?seats=<variantId,variantId,...> loads a whole plate (the /pages/plates "Swap any piece" link); it replaces
    // whatever was saved. Unknown or sold-out ids are skipped; the plate rules still apply in #validSeats.
    let fromSeats = false;
    const seatIds = (params.get('seats') || '').split(',').map((x) => x.trim()).filter((x) => /^\d+$/.test(x));
    if (seatIds.length) {
      const owner = new Map();
      for (const p of this.#data.products || []) for (const v of p.vars || []) owner.set(String(v.id), p);
      const loaded = [];
      for (const id of seatIds) {
        const p = owner.get(id);
        if (p) loaded.push({ h: p.h, v: id });
      }
      if (loaded.length) {
        seats = loaded;
        fromSeats = true;
        if (!SIZES.includes(askedSize)) size = SIZES.find((n) => n >= loaded.length) || SIZES[SIZES.length - 1];
      }
    }

    let prefilled = null;
    const prefill = params.get('prefill');
    if (prefill && this.#byHandle.has(prefill) && !fromSeats) {
      const p = this.#byHandle.get(prefill);
      const wanted = this.#variant(p, params.get('variant'));
      const v = wanted && wanted.a ? wanted : this.#firstAvailable(p);
      if (v) {
        const rest = seats.filter((s) => !(s.h === p.h && String(s.v) === String(v.id)));
        prefilled = { p, v };
        seats = [{ h: p.h, v: v.id }, ...rest];
      }
    }
    if (params.has('prefill') || params.has('seats')) {
      // Don't re-insert on reload: from here on the saved plate is the source of truth.
      params.delete('prefill');
      params.delete('variant');
      params.delete('seats');
      params.delete('plate');
      const qs = params.toString();
      window.history.replaceState(window.history.state, '', `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`);
    }

    // The free Christmas Mini is opt-in (bible 4.7: an unticked box): it starts unticked on a first visit, and a
    // saved plate keeps the shopper's own choice.
    const mini = saved && typeof saved.mini === 'boolean' ? saved.mini : false;
    const state = { size, seats: this.#validSeats(seats, size), mini };

    // Say what the link did. A saved plate is never thrown away silently: it stays, with a one-tap "Start fresh".
    if (prefilled && state.seats[0]?.h === prefilled.p.h) {
      // The top banner says it ("<Item> is in seat 1. 7 seats left.") and offers the one-tap finish.
      const kept = state.seats.length - 1;
      this.#pre = { h: prefilled.p.h, kept, picked: false };
      if (kept > 0) {
        this.#undo = { size: state.size, seats: [state.seats[0]], mini: state.mini };
        this.#undoLabel = 'Start fresh';
        this.#undoSay = `Fresh plate: just the ${prefilled.p.t} in seat 1. Pick the other ${state.size - 1}.`;
      }
    } else if (fromSeats) {
      const left = state.size - state.seats.length;
      this.#hello = {
        message: left
          ? `Plate loaded: ${state.seats.length} of ${state.size}. Pick ${left} more, or tap a seat to swap it.`
          : 'Plate loaded. Tap any seat to swap a piece.',
      };
    }
    return state;
  }

  /* ------------------------------------------------------------------ */
  /* Events                                                              */
  /* ------------------------------------------------------------------ */

  #wire() {
    const { signal } = /** @type {AbortController} */ (this.#ac);

    this.addEventListener(
      'change',
      (event) => {
        const t = event.target;
        if (!(t instanceof HTMLInputElement)) return;
        if (t.matches('[data-sd-pb-size]')) this.#setSize(Number(t.value));
        if (t.matches('[data-sd-pb-mini]')) {
          this.#state.mini = t.checked;
          this.#render();
          this.#say(t.checked ? 'Free Christmas Mini added to your platter.' : 'Free Christmas Mini removed.');
        }
      },
      { signal }
    );

    this.addEventListener(
      'click',
      (event) => {
        const t = event.target instanceof Element ? event.target : null;
        if (!t) return;
        const view = t.closest('[data-sd-pb-view]');
        if (view instanceof HTMLElement) return this.#onView(view.dataset.sdPbView || '', view);
        if (t.closest('[data-sd-pb-sheet-add]')) return this.#addFromView();
        const add = t.closest('[data-sd-pb-add]');
        if (add instanceof HTMLElement) return this.#onAdd(add.dataset.sdPbAdd || '', add);
        const seat = t.closest('[data-sd-pb-seat]');
        if (seat instanceof HTMLElement) return this.#onSeat(Number(seat.dataset.sdPbSeat), seat);
        const filter = t.closest('[data-sd-pb-filter]');
        if (filter instanceof HTMLElement) return this.#filter(filter.dataset.sdPbFilter || 'all');
        if (t.closest('[data-sd-pb-surprise]')) return this.#surprise();
        if (t.closest('[data-sd-pb-pick]')) return this.#fillPick();
        if (t.closest('[data-sd-pb-self]')) return this.#pickMyself();
        if (t.closest('[data-sd-pb-fresh]')) return this.#startFresh();
        if (t.closest('[data-sd-pb-clear]')) return this.#clear();
        if (t.closest('[data-sd-pb-undo]')) return this.#restoreUndo();
        const cta = t.closest('[data-sd-pb-cta]');
        if (cta instanceof HTMLElement) return this.#addToBag(cta);
        if (t.closest('[data-sd-pb-share]')) return this.#share();
        const chip = t.closest('[data-sd-pb-variant]');
        if (chip instanceof HTMLElement) return this.#onChip(chip.dataset.sdPbVariant || '');
        if (t.closest('[data-sd-pb-sheet-remove]')) return this.#removeFromSheet();
        if (t.closest('[data-sd-pb-sheet-close]')) return this.#closeSheet();
      },
      { signal }
    );

    // Intent (hover, touch start, focus) on a menu item warms its style pictures before the sheet opens;
    // on a style chip it previews that style in the sheet header.
    const warm = (event) => {
      const t = event.target instanceof Element ? event.target : null;
      const chip = t?.closest('[data-sd-pb-variant]');
      if (chip instanceof HTMLElement) return this.#previewChip(chip);
      const add = t?.closest('[data-sd-pb-add]');
      const p = add instanceof HTMLElement && this.#byHandle.get(add.dataset.sdPbAdd || '');
      if (p) for (const v of p.vars) if (v.a) preload(this.#imgOf(p, v.id));
    };
    this.addEventListener('pointerover', warm, { signal, passive: true });
    this.addEventListener('pointerdown', warm, { signal, passive: true });
    this.addEventListener('focusin', warm, { signal });

    const sheet = this.querySelector('[data-sd-pb-sheet]');
    if (sheet instanceof HTMLDialogElement) {
      // Tap on the backdrop closes the sheet.
      sheet.addEventListener(
        'click',
        (event) => {
          if (event.target === sheet) this.#closeSheet();
        },
        { signal }
      );
      sheet.addEventListener('close', () => this.#afterSheetClose(), { signal });
    }

    // Another tab or window changed the saved plate.
    window.addEventListener(
      'storage',
      (event) => {
        if (event.key !== STORE_KEY || this.#busy) return;
        const saved = readStore();
        if (!saved) return;
        const size = SIZES.includes(Number(saved.size)) ? Number(saved.size) : this.#state.size;
        this.#state = { size, seats: this.#validSeats(saved.seats || [], size), mini: Boolean(saved.mini) };
        this.#render({ save: false });
      },
      { signal }
    );
  }

  #setSize(n) {
    if (!SIZES.includes(n) || n === this.#state.size) return;
    const before = structuredClone(this.#state);
    const had = this.#state.seats.length;
    this.#state.size = n;
    this.#state.seats = this.#validSeats(this.#state.seats.slice(0, n), n);
    const name = this.#size.name;
    if (had > n) {
      this.#undo = before;
      this.#resetUndoLabel();
      this.#say(`Switched to the ${name}. We kept your first ${n} pieces.`, { undo: true });
    } else {
      this.#undo = null;
      this.#resetUndoLabel();
      this.#say(`${name}: ${n} seats, ${formatMoney(this.#size.each)} each.`);
    }
    this.#render();
  }

  #onAdd(handle, trigger) {
    const p = this.#byHandle.get(handle);
    if (!p) return;
    const why = this.#why(p);
    if (why) {
      this.#say(this.#whyText(why));
      if (trigger instanceof HTMLElement) this.#nudge(trigger);
      return;
    }
    const available = p.vars.filter((v) => v.a);
    if (available.length > 1) {
      this.#openSheet({ mode: 'add', h: p.h, seat: this.#state.seats.length, trigger });
    } else {
      this.#put(p, available[0].id);
    }
  }

  /** "View": the big picture and every style, without putting anything on the plate yet. */
  #onView(handle, trigger) {
    const p = this.#byHandle.get(handle);
    if (!p) return;
    const sel = (this.#firstAvailable(p) || p.vars[0])?.id;
    this.#openSheet({ mode: 'view', h: p.h, seat: this.#state.seats.length, trigger, sel });
  }

  #addFromView() {
    const ctx = this.#sheetCtx;
    const p = ctx && this.#byHandle.get(ctx.h);
    if (!ctx || ctx.mode !== 'view' || !p) return;
    const v = this.#variant(p, ctx.sel);
    this.#closeSheet({ focus: false });
    const why = this.#why(p);
    if (why) {
      this.#say(this.#whyText(why));
      this.#refocus(ctx.trigger);
      return;
    }
    if (!v || !v.a) return;
    this.#put(p, v.id);
    this.#refocus(ctx.trigger);
  }

  #viewLabel(sheet, p, ctx) {
    const v = this.#variant(p, ctx.sel);
    const multi = p.vars.length > 1;
    const why = this.#why(p);
    this.#text(
      sheet,
      '[data-sd-pb-sheet-add-text]',
      why ? this.#whyShort(why) : multi && v ? `Add ${v.t} to my plate` : 'Add to my plate'
    );
  }

  #put(p, variantId) {
    this.#undo = null;
    this.#resetUndoLabel();
    preload(this.#imgOf(p, variantId));
    this.#state.seats.push({ h: p.h, v: variantId });
    const n = this.#state.seats.length;
    const left = this.#state.size - n;
    const name = this.#style(p, variantId);
    const style = name ? `, ${name},` : '';
    this.#render({ pop: [n - 1] });
    this.#say(
      left > 0
        ? `${p.t}${style} is in seat ${n}. ${left} ${plural(left, 'seat')} left.`
        : `${p.t}${style} is in seat ${n}. Your ${this.#size.name} is full.`
    );
    if (left === 0) this.#celebrate();
  }

  #onSeat(index, trigger) {
    const seat = this.#state.seats[index];
    if (!seat) {
      // Empty seat: send the shopper to the menu.
      const heading = this.querySelector('[data-sd-pb-menu-heading]');
      if (heading instanceof HTMLElement) {
        heading.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' });
        heading.focus({ preventScroll: true });
      }
      return;
    }
    this.#openSheet({ mode: 'edit', h: seat.h, seat: index, trigger });
  }

  #filter(key) {
    for (const chip of this.querySelectorAll('[data-sd-pb-filter]')) {
      chip.setAttribute('aria-pressed', String(chip.getAttribute('data-sd-pb-filter') === key));
    }
    for (const item of this.querySelectorAll('[data-sd-pb-item]')) {
      if (item instanceof HTMLElement) item.hidden = key !== 'all' && item.dataset.counter !== key;
    }
  }

  #surprise() {
    const s = this.#state;
    const before = structuredClone(s);
    const start = s.seats.length;
    const pool = this.#data.products.filter((p) => p.vars.some((v) => v.a));
    let guard = 0;
    while (s.seats.length < s.size && guard < 200) {
      guard += 1;
      const fits = pool.filter((p) => !this.#why(p));
      if (!fits.length) break;
      // Prefer pieces that aren't on the plate yet, so a surprise plate is varied.
      const fresh = fits.filter((p) => !s.seats.some((seat) => seat.h === p.h));
      const p = pick(fresh.length ? fresh : fits);
      const v = pick(p.vars.filter((x) => x.a));
      s.seats.push({ h: p.h, v: v.id });
      preload(this.#imgOf(p, v.id));
    }
    const added = s.seats.length - start;
    if (!added) {
      this.#say(s.seats.length >= s.size ? `Your ${this.#size.name} is already full.` : this.#whyText('weight'));
      return;
    }
    this.#undo = before;
    this.#resetUndoLabel();
    const pop = Array.from({ length: added }, (_, i) => start + i);
    this.#render({ pop });
    const left = s.size - s.seats.length;
    this.#say(
      left
        ? `Surprise: we filled ${added} ${plural(added, 'seat')} at random. The other ${left} would take this plate over its weight cap, so pick lighter pieces.`
        : `Surprise: we filled ${added} ${plural(added, 'seat')} at random. Tap any seat to swap it.`,
      { undo: true }
    );
    if (!left) this.#celebrate();
  }

  #clear() {
    if (!this.#state.seats.length) return;
    this.#undo = structuredClone(this.#state);
    this.#resetUndoLabel();
    this.#state.seats = [];
    this.#render();
    this.#say('Plate cleared.', { undo: true });
  }

  /** "Fill the rest with our pick": the section's pick list (Every Counter, then spares), skipping anything already
   *  on the plate and anything the plate limits refuse; then the first pieces that fit, in counter order. */
  #fillPick() {
    const s = this.#state;
    const before = structuredClone(s);
    const start = s.seats.length;
    const want = String(this.#data.pick || '')
      .split(/\r?\n/)
      .map((line) => {
        const at = line.indexOf(':');
        return at < 0 ? { h: line.trim(), t: '' } : { h: line.slice(0, at).trim(), t: line.slice(at + 1).trim() };
      })
      .filter((x) => x.h);
    const tryPut = (p, title, fresh) => {
      if (!p || s.seats.length >= s.size) return;
      if (fresh && s.seats.some((seat) => seat.h === p.h)) return;
      if (this.#why(p)) return;
      const named = title && p.vars.find((v) => v.a && v.t.toLowerCase() === title.toLowerCase());
      const v = named || this.#firstAvailable(p);
      if (!v) return;
      s.seats.push({ h: p.h, v: v.id });
      preload(this.#imgOf(p, v.id));
    };
    for (const x of want) tryPut(this.#byHandle.get(x.h), x.t, true);
    for (const p of this.#data.products || []) tryPut(p, '', true);
    for (const p of this.#data.products || []) tryPut(p, '', false);
    const added = s.seats.length - start;
    if (!added) {
      this.#say(s.seats.length >= s.size ? `Your ${this.#size.name} is already full.` : this.#whyText('weight'));
      return;
    }
    this.#undo = before;
    this.#resetUndoLabel();
    if (this.#pre) this.#pre.picked = true;
    const pop = Array.from({ length: added }, (_, i) => start + i);
    this.#render({ pop });
    const left = s.size - s.seats.length;
    this.#say(
      left
        ? `We filled ${added} ${plural(added, 'seat')}. The other ${left} would go over this plate's weight cap.`
        : `We filled ${added} ${plural(added, 'seat')} with our pick. Tap any seat to swap it.`,
      { undo: true }
    );
    if (!left) this.#celebrate();
    const title = this.querySelector('[data-sd-pb-pre-title]');
    if (title instanceof HTMLElement && this.#pre) title.focus({ preventScroll: true });
  }

  /** "Pick them myself" (or "See the plate"): to the menu while seats are empty, else to the plate. */
  #pickMyself() {
    const full = this.#state.seats.length >= this.#state.size;
    if (!full) {
      this.#pre = null;
      this.#renderPre();
    }
    const target = this.querySelector(full ? '[data-sd-pb-plate]' : '[data-sd-pb-menu-heading]');
    if (target instanceof HTMLElement) {
      target.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: full ? 'center' : 'start' });
      if (!full) target.focus({ preventScroll: true });
    }
  }

  #startFresh() {
    if (this.#pre) this.#pre.kept = 0;
    this.#restoreUndo();
  }

  #restoreUndo() {
    if (!this.#undo) return;
    if (this.#pre) this.#pre.picked = false;
    this.#state = this.#undo;
    this.#undo = null;
    const message = this.#undoSay;
    this.#resetUndoLabel();
    this.#render();
    this.#say(message);
  }

  #resetUndoLabel() {
    this.#undoLabel = 'Undo';
    this.#undoSay = 'Undone.';
  }

  /* ------------------------------------------------------------------ */
  /* Seat sheet (style chips per seat)                                    */
  /* ------------------------------------------------------------------ */

  #openSheet(ctx) {
    const sheet = this.querySelector('[data-sd-pb-sheet]');
    const p = this.#byHandle.get(ctx.h);
    if (!(sheet instanceof HTMLDialogElement) || !p) return;
    this.#sheetCtx = ctx;
    const current = ctx.mode === 'edit' ? this.#state.seats[ctx.seat] : null;
    const seatN = ctx.seat + 1;
    const viewing = ctx.mode === 'view';

    // Edit: the exact style on that seat. View: the selected style. Add: the product picture until a style chip is
    // hovered or focused.
    const shown = current ? current.v : viewing ? ctx.sel : null;
    this.#sheetImage(sheet, shown != null ? this.#imgOf(p, shown) : p.img || this.#imgOf(p, this.#firstAvailable(p)?.id), p, shown);
    this.#text(sheet, '[data-sd-pb-sheet-eyebrow]', viewing ? p.c || '' : `Seat ${seatN}${p.c ? ` · ${p.c}` : ''}`);
    this.#text(sheet, '[data-sd-pb-sheet-title]', p.t);
    this.#text(sheet, '[data-sd-pb-sheet-desc]', p.d || '');

    const styles = sheet.querySelector('[data-sd-pb-sheet-styles]');
    const chips = sheet.querySelector('[data-sd-pb-sheet-chips]');
    const multi = p.vars.length > 1;
    if (styles instanceof HTMLElement) styles.hidden = !multi;
    this.#text(
      sheet,
      '[data-sd-pb-sheet-legend]',
      ctx.mode === 'add' ? `Pick a style for seat ${seatN}` : viewing ? `${p.vars.length} styles` : 'Style'
    );
    if (chips && !multi) chips.innerHTML = '';
    if (chips && multi) {
      chips.innerHTML = p.vars
        .map((v) => {
          const on = current ? String(current.v) === String(v.id) : viewing ? String(ctx.sel) === String(v.id) : false;
          const src = this.#imgOf(p, v.id);
          const thumb = src
            ? `<span class="sd-pb-sheet__thumb"><img src="${esc(src)}" alt="" width="40" height="40" decoding="async"></span>`
            : '';
          return `<button type="button" class="sd-chip sd-pb-sheet__chip${src ? ' sd-pb-sheet__chip--img' : ''}" data-sd-pb-variant="${v.id}" aria-pressed="${on}"${
            v.a ? '' : ' disabled'
          }>${thumb}<span class="sd-pb-sheet__chip-name">${esc(v.t)}</span>${v.a ? '' : ' <span class="sd-pb-sheet__soldout">sold out</span>'}</button>`;
        })
        .join('');
    }

    const note = sheet.querySelector('[data-sd-pb-sheet-note]');
    if (note instanceof HTMLElement) {
      const lines = [];
      if (p.mystery) lines.push("Baker's choice. We don't pick either. Random style; duplicates can happen.");
      if (p.vars.some((v) => /surprise/i.test(v.t))) lines.push('"Surprise me" means a random style, picked at the warehouse.');
      if (ctx.mode === 'add' && multi) lines.push('Tap a style to put it on the plate.');
      if (viewing && multi) lines.push('Tap a style to see it bigger.');
      note.hidden = !lines.length;
      note.textContent = lines.join(' ');
    }

    const remove = sheet.querySelector('[data-sd-pb-sheet-remove]');
    if (remove instanceof HTMLElement) remove.hidden = ctx.mode !== 'edit';
    const addBtn = sheet.querySelector('[data-sd-pb-sheet-add]');
    if (addBtn instanceof HTMLElement) addBtn.hidden = !viewing;
    if (viewing) this.#viewLabel(sheet, p, ctx);
    const link = sheet.querySelector('[data-sd-pb-sheet-link]');
    if (link instanceof HTMLAnchorElement) link.href = `/products/${encodeURIComponent(p.h)}`;
    this.#text(sheet, '[data-sd-pb-sheet-done]', ctx.mode === 'edit' ? 'Done' : viewing ? 'Close' : 'Cancel');

    if (chips) for (const thumb of chips.querySelectorAll('img')) reveal(thumb);
    sheet.showModal();
    const focusTarget =
      (viewing && sheet.querySelector('[data-sd-pb-sheet-add]')) ||
      sheet.querySelector('.sd-pb-sheet__chip[aria-pressed="true"]') ||
      sheet.querySelector('.sd-pb-sheet__chip:not([disabled])') ||
      sheet.querySelector('[data-sd-pb-sheet-done]');
    // Open at the top (big picture and close button in view); focus moves without scrolling the sheet.
    const inner = sheet.querySelector('.sd-pb-sheet__inner');
    if (inner instanceof HTMLElement) inner.scrollTop = 0;
    if (focusTarget instanceof HTMLElement) focusTarget.focus({ preventScroll: true });
  }

  #sheetImage(sheet, src, p = null, variantId = null) {
    const img = sheet.querySelector('[data-sd-pb-sheet-img]');
    if (!(img instanceof HTMLImageElement)) return;
    const next = bigImg(src) || BLANK;
    const v = p && variantId != null ? this.#variant(p, variantId) : null;
    img.alt = p ? `${p.t}${v && p.vars.length > 1 ? `, ${v.t}` : ''}` : '';
    if (img.getAttribute('src') === next) return;
    img.classList.remove('is-loaded');
    img.src = next;
    if (src) reveal(img);
  }

  /** Hover or focus on a style chip previews that exact style in the sheet header. */
  #previewChip(chip) {
    const ctx = this.#sheetCtx;
    const p = ctx && this.#byHandle.get(ctx.h);
    const sheet = this.querySelector('[data-sd-pb-sheet]');
    if (!p || !(sheet instanceof HTMLElement)) return;
    this.#sheetImage(sheet, this.#imgOf(p, chip.dataset.sdPbVariant), p, chip.dataset.sdPbVariant);
  }

  #onChip(variantId) {
    const ctx = this.#sheetCtx;
    const p = ctx && this.#byHandle.get(ctx.h);
    if (!ctx || !p) return;
    const v = this.#variant(p, variantId);
    if (!v || !v.a) return;
    if (ctx.mode === 'view') {
      // View: tapping a style selects it and shows it big; the Add button puts it on the plate.
      ctx.sel = v.id;
      const sheet = this.querySelector('[data-sd-pb-sheet]');
      if (sheet instanceof HTMLElement) {
        for (const c of sheet.querySelectorAll('[data-sd-pb-variant]')) {
          c.setAttribute('aria-pressed', String(String(c.getAttribute('data-sd-pb-variant')) === String(v.id)));
        }
        this.#sheetImage(sheet, this.#imgOf(p, v.id), p, v.id);
        this.#viewLabel(sheet, p, ctx);
      }
      return;
    }
    if (ctx.mode === 'add') {
      this.#closeSheet({ focus: false });
      const why = this.#why(p);
      if (why) return this.#say(this.#whyText(why));
      this.#put(p, v.id);
      this.#refocus(ctx.trigger);
      return;
    }
    const seat = this.#state.seats[ctx.seat];
    if (seat) {
      seat.v = v.id;
      this.#render({ pop: [ctx.seat] });
      this.#say(`Seat ${ctx.seat + 1} is now ${p.t}, ${v.t}.`);
    }
    this.#closeSheet();
  }

  #removeFromSheet() {
    const ctx = this.#sheetCtx;
    if (!ctx || ctx.mode !== 'edit') return;
    const p = this.#byHandle.get(ctx.h);
    this.#undo = structuredClone(this.#state);
    this.#resetUndoLabel();
    this.#state.seats.splice(ctx.seat, 1);
    this.#closeSheet();
    this.#render();
    this.#say(`${p ? p.t : 'That piece'} is off the plate.`, { undo: true });
  }

  #closeSheet({ focus = true } = {}) {
    const sheet = this.querySelector('[data-sd-pb-sheet]');
    if (!(sheet instanceof HTMLDialogElement) || !sheet.open) return;
    if (!focus && this.#sheetCtx) this.#sheetCtx.noFocus = true;
    sheet.close();
  }

  #afterSheetClose() {
    const ctx = this.#sheetCtx;
    this.#sheetCtx = null;
    if (ctx && !ctx.noFocus) this.#refocus(ctx.trigger, ctx);
  }

  #refocus(trigger, ctx) {
    if (trigger instanceof HTMLElement && trigger.isConnected && !trigger.closest('[hidden]')) {
      trigger.focus({ preventScroll: true });
      return;
    }
    if (ctx) {
      const seat = this.querySelector(`[data-sd-pb-seat="${Math.min(ctx.seat, this.#state.size - 1)}"]`);
      if (seat instanceof HTMLElement) seat.focus({ preventScroll: true });
    }
  }

  /* ------------------------------------------------------------------ */
  /* Bag + share                                                         */
  /* ------------------------------------------------------------------ */

  #lines() {
    const counts = new Map();
    for (const seat of this.#state.seats) counts.set(String(seat.v), (counts.get(String(seat.v)) || 0) + 1);
    const items = [...counts].map(([id, quantity]) => ({ id: Number(id), quantity }));
    // The Mini is only free with a full Party Platter, so it only rides along once every seat is filled.
    if (this.#miniOn() && this.#state.seats.length === this.#state.size) {
      items.push({ id: Number(this.#data.mini.id), quantity: 1 });
    }
    return items;
  }

  async #addToBag(button) {
    if (this.#busy) return;
    const s = this.#state;
    const errors = [...this.querySelectorAll('[data-sd-pb-error]')];
    const left = s.size - s.seats.length;
    if (left > 0) {
      this.#say(`${left} ${plural(left, 'seat')} left. Pick ${left} more, or tap Surprise me.`);
      this.#nudge(button);
      // Take people (and keyboard focus) straight to the squishies instead of leaving them at a dead end.
      const heading = this.querySelector('[data-sd-pb-menu-heading]');
      if (heading instanceof HTMLElement) {
        heading.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' });
        heading.focus({ preventScroll: true });
      }
      return;
    }
    const items = this.#lines();
    const ctas = [...this.querySelectorAll('[data-sd-pb-cta]')];
    this.#busy = true;
    for (const error of errors) error.textContent = '';
    for (const cta of ctas) {
      cta.setAttribute('aria-busy', 'true');
      cta.classList.add('is-loading');
      this.#text(cta, '[data-sd-pb-cta-text]', 'Adding…');
    }
    try {
      await addItems(items, { source: this });
      clearStore();
      squish(button);
      for (const cta of ctas) {
        cta.classList.add('is-added');
        this.#text(cta, '[data-sd-pb-cta-text]', 'In your bag');
      }
      this.#say(`Your ${this.#size.name} is in your bag.`);
      window.setTimeout(() => {
        for (const cta of ctas) cta.classList.remove('is-added');
        this.#renderSummary();
      }, 2400);
    } catch (err) {
      // Clear the loading state first: #renderSummary skips buttons that are still aria-busy.
      for (const cta of ctas) {
        cta.removeAttribute('aria-busy');
        cta.classList.remove('is-loading');
      }
      const reason = (err instanceof Error && err.message ? err.message : 'Something went wrong.').replace(/\s*Please try again\.?$/i, '');
      const message = `${reason} Your plate is saved. Tap Try again.`;
      // The message sits under the button that was pressed (the bar has none: the ticket's, plus the toast).
      const near =
        button.parentElement?.querySelector('[data-sd-pb-error]') ||
        this.querySelector('[data-sd-pb-ticket] [data-sd-pb-error]') ||
        errors[0];
      for (const error of errors) error.textContent = error === near ? message : '';
      this.#say(message);
      this.#renderSummary();
      for (const cta of ctas) this.#text(cta, '[data-sd-pb-cta-text]', `Try again · ${formatMoney(this.#size.each * s.size)}`);
    } finally {
      this.#busy = false;
      for (const cta of ctas) {
        cta.removeAttribute('aria-busy');
        cta.classList.remove('is-loading');
      }
    }
  }

  async #share() {
    const status = this.querySelector('[data-sd-pb-share-status]');
    const field = this.querySelector('[data-sd-pb-share-url]');
    const left = this.#state.size - this.#state.seats.length;
    if (left > 0) {
      const message = `${left} ${plural(left, 'seat')} left. Pick ${left} more, or tap Surprise me.`;
      if (status) status.textContent = message;
      this.#say(message);
      return;
    }
    const items = this.#lines();
    const url = `${window.location.origin}/cart/${items.map((i) => `${i.id}:${i.quantity}`).join(',')}`;
    const n = this.#state.seats.length;
    const text = `My Squish Deli ${this.#size.name}: ${n} ${plural(n, 'squishy', 'squishies')}. Looks delicious. Do not eat.`;
    if (navigator.share && window.matchMedia('(pointer: coarse)').matches) {
      try {
        await navigator.share({ title: 'My Squish Deli plate', text, url });
        if (status) status.textContent = 'Plate sent.';
        return;
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      if (status) status.textContent = 'Link copied. It opens checkout with this plate.';
      if (field instanceof HTMLInputElement) field.hidden = true;
    } catch {
      if (field instanceof HTMLInputElement) {
        field.hidden = false;
        field.value = url;
        field.focus();
        field.select();
      }
      if (status) status.textContent = 'Copy this link. It opens checkout with this plate.';
    }
  }

  /* ------------------------------------------------------------------ */
  /* Rendering                                                           */
  /* ------------------------------------------------------------------ */

  #text(root, selector, value) {
    const el = root.querySelector(selector);
    if (el && el.textContent !== value) el.textContent = value;
  }

  #render({ pop = [], save = true } = {}) {
    this.#renderSizes();
    this.#renderPlate(pop);
    this.#renderMenu();
    this.#renderSummary(pop);
    this.#renderPre();
    if (save) writeStore(this.#state);
  }

  /** The product-page arrival banner: "<Item> is in seat 1. 7 seats left." until full, then "ready" + the CTA. */
  #renderPre() {
    const box = this.querySelector('[data-sd-pb-pre]');
    if (!(box instanceof HTMLElement)) return;
    const pre = this.#pre;
    const s = this.#state;
    const first = s.seats[0];
    const p = pre && first && this.#byHandle.get(first.h);
    box.hidden = !p;
    if (!pre || !p) return;
    const size = this.#size;
    const left = s.size - s.seats.length;
    const full = left === 0;
    const style = this.#style(p, first.v);
    const img = box.querySelector('[data-sd-pb-pre-img]');
    const src = this.#imgOf(p, first.v);
    if (img instanceof HTMLImageElement && src && img.getAttribute('src') !== src) {
      img.classList.remove('is-loaded');
      img.src = src;
      reveal(img);
    }
    const ask = box.querySelector('[data-sd-pb-pre-ask]');
    const done = box.querySelector('[data-sd-pb-pre-done]');
    if (ask instanceof HTMLElement) ask.hidden = full;
    if (done instanceof HTMLElement) done.hidden = !full;
    box.classList.toggle('is-full', full);
    if (full) {
      this.#text(box, '[data-sd-pb-pre-title]', `Your ${size.name} is ready.`);
      this.#text(
        box,
        '[data-sd-pb-pre-sub]',
        `${p.t} in seat 1${pre.picked ? `, plus ${s.size - 1} from our pick` : ''}. ${formatMoney(size.each * s.size)} for ${s.size}, ships free.`
      );
    } else {
      const kept = Math.min(pre.kept, s.seats.length - 1);
      this.#text(box, '[data-sd-pb-pre-title]', `${p.t}${style ? ` (${style})` : ''} is in seat 1.`);
      this.#text(
        box,
        '[data-sd-pb-pre-sub]',
        `${kept > 0 ? `We kept ${kept} ${plural(kept, 'piece')} from your last plate. ` : ''}${left} ${plural(left, 'seat')} left on your ${size.name}, ${formatMoney(size.each)} each.`
      );
      const fresh = box.querySelector('[data-sd-pb-fresh]');
      if (fresh instanceof HTMLElement) fresh.hidden = !(kept > 0 && this.#undo);
    }
    const thumbs = box.querySelector('[data-sd-pb-pre-thumbs]');
    if (thumbs) {
      const html = Array.from({ length: s.size }, (_, i) => {
        const seat = s.seats[i];
        const sp = seat && this.#byHandle.get(seat.h);
        const tsrc = sp ? this.#imgOf(sp, seat.v) : '';
        return tsrc
          ? `<li class="sd-pb-pre__thumb is-filled"><img src="${esc(tsrc)}" alt="" width="40" height="40" decoding="async"></li>`
          : `<li class="sd-pb-pre__thumb"><span>${i + 1}</span></li>`;
      }).join('');
      if (thumbs.innerHTML !== html) {
        thumbs.innerHTML = html;
        for (const t of thumbs.querySelectorAll('img')) reveal(t);
      }
    }
  }

  #separately(n) {
    const seats = this.#state.seats.slice(0, n);
    const known = seats.reduce((sum, seat) => sum + (this.#byHandle.get(seat.h)?.price || this.#data.single), 0);
    return known + Math.max(0, n - seats.length) * this.#data.single;
  }

  #renderSizes() {
    for (const input of this.querySelectorAll('[data-sd-pb-size]')) {
      if (input instanceof HTMLInputElement) input.checked = Number(input.value) === this.#state.size;
    }
    for (const el of this.querySelectorAll('[data-sd-pb-sep]')) {
      const n = Number(el.getAttribute('data-sd-pb-sep'));
      const value = formatMoney(this.#separately(n));
      if (el.textContent !== value) el.textContent = value;
    }
    const offer = this.querySelector('[data-sd-pb-offer]');
    if (offer instanceof HTMLElement) offer.hidden = !(this.#state.size === 8 && this.#offerLive());
    for (const mini of this.querySelectorAll('[data-sd-pb-mini]')) {
      if (mini instanceof HTMLInputElement) mini.checked = this.#state.mini;
    }
    const preMini = this.querySelector('.sd-pb-pre__mini');
    if (preMini instanceof HTMLElement) preMini.hidden = !(this.#state.size === 8 && this.#offerLive());
  }

  #renderPlate(pop) {
    const s = this.#state;
    const plate = this.querySelector('[data-sd-pb-plate]');
    if (plate instanceof HTMLElement) {
      plate.dataset.size = String(s.size);
      plate.setAttribute('aria-label', `Your ${this.#size.name}: ${s.seats.length} of ${s.size} seats filled`);
    }
    this.#text(this, '[data-sd-pb-center]', `${s.seats.length}/${s.size}`);
    const wraps = [...this.querySelectorAll('[data-sd-pb-seat-wrap]')];
    wraps.forEach((wrap, i) => {
      if (!(wrap instanceof HTMLElement)) return;
      wrap.hidden = i >= s.size;
      const button = wrap.querySelector('[data-sd-pb-seat]');
      if (!(button instanceof HTMLElement)) return;
      const seat = s.seats[i];
      const p = seat && this.#byHandle.get(seat.h);
      const next = i === s.seats.length && i < s.size;
      button.classList.toggle('is-filled', Boolean(p));
      button.classList.toggle('is-next', next);
      const key = p ? `${p.h}:${seat.v}` : '';
      if (button.dataset.key !== key) {
        button.dataset.key = key;
        if (p) {
          const style = this.#style(p, seat.v);
          const src = this.#imgOf(p, seat.v);
          // The initial sits under the photo, so a seat is never an empty circle while the picture arrives.
          const initial = p.t.replace(/^The\s+/i, '').charAt(0);
          button.innerHTML = `<span class="sd-pb-seat__ph" aria-hidden="true">${esc(initial)}</span>${
            src ? `<img class="sd-pb-seat__img" src="${esc(src)}" alt="" width="100" height="100" decoding="async">` : ''
          }<span class="sd-pb-seat__n" aria-hidden="true">${i + 1}</span>`;
          reveal(button.querySelector('img'));
          button.setAttribute('aria-label', `Seat ${i + 1}: ${p.t}${style ? `, ${style}` : ''}. Change style or remove.`);
        } else {
          button.innerHTML = `<span class="sd-pb-seat__n" aria-hidden="true">${i + 1}</span>`;
          button.setAttribute('aria-label', `Seat ${i + 1}: empty. Choose a squish from the menu.`);
        }
      }
      if (pop.includes(i) && p && !reduceMotion()) this.#pop(button);
    });

    const left = s.size - s.seats.length;
    const noun = this.#noun();
    this.#text(
      this,
      '[data-sd-pb-progress]',
      left > 0
        ? `${s.seats.length} of ${s.size} on the ${noun}, ${left} ${plural(left, 'seat')} left`
        : `${s.size} of ${s.size} on the ${noun}. Ready when you are.`
    );
  }

  #renderMenu() {
    const each = formatMoney(this.#size.each);
    for (const item of this.querySelectorAll('[data-sd-pb-item]')) {
      if (!(item instanceof HTMLElement)) continue;
      const p = this.#byHandle.get(item.dataset.sdPbItem || '');
      if (!p) continue;
      const count = this.#state.seats.filter((seat) => seat.h === p.h).length;
      const why = this.#why(p);
      const button = item.querySelector('[data-sd-pb-add]');
      const badge = item.querySelector('[data-sd-pb-count]');
      item.classList.toggle('is-on', count > 0);
      item.classList.toggle('is-blocked', Boolean(why));
      if (badge instanceof HTMLElement) {
        badge.hidden = count === 0;
        badge.textContent = count ? `×${count} on plate` : '';
      }
      this.#text(item, '[data-sd-pb-each]', each);
      this.#text(item, '[data-sd-pb-cta-label]', why ? this.#whyShort(why) : 'Add');
      if (button instanceof HTMLElement) {
        if (why) button.setAttribute('aria-disabled', 'true');
        else button.removeAttribute('aria-disabled');
        const on = count ? ` ${count} on your plate.` : '';
        button.setAttribute(
          'aria-label',
          why ? `${p.t}: ${this.#whyShort(why)}.${on}` : `Add ${p.t} to your plate, ${each} on this plate.${on}`
        );
      }
    }
  }

  #renderSummary(pop = []) {
    const s = this.#state;
    const size = this.#size;
    const filled = s.seats.length;
    const left = s.size - filled;
    const total = size.each * s.size;
    const noun = this.#noun();
    const full = left === 0;

    // Bar thumbnails
    const thumbs = this.querySelector('[data-sd-pb-thumbs]');
    if (thumbs) {
      const html = Array.from({ length: s.size }, (_, i) => {
        const seat = s.seats[i];
        const p = seat && this.#byHandle.get(seat.h);
        const src = p ? this.#imgOf(p, seat.v) : '';
        return src
          ? `<li class="sd-pb-bar__thumb is-filled${pop.includes(i) ? ' is-pop' : ''}"><img src="${esc(src)}" alt="" width="40" height="40" decoding="async"></li>`
          : `<li class="sd-pb-bar__thumb${p ? ' is-filled' : ''}"></li>`;
      }).join('');
      if (thumbs.innerHTML !== html) {
        thumbs.innerHTML = html;
        for (const img of thumbs.querySelectorAll('img')) reveal(img);
      }
    }
    this.#text(this, '[data-sd-pb-bar-count]', full ? `${s.size} of ${s.size}: ready` : `${filled} of ${s.size} on the ${noun}`);
    this.#text(this, '[data-sd-pb-bar-total]', formatMoney(total));
    this.#text(this, '[data-sd-pb-bar-each]', `${formatMoney(size.each)} each · ships free`);

    // CTAs (bar + ticket)
    // Never a dead button: before the plate is full it says how many are left and takes you to the menu.
    const label = full ? `Add ${noun} to bag · ${formatMoney(total)}` : `Pick ${left} more`;
    for (const cta of this.querySelectorAll('[data-sd-pb-cta]')) {
      if (cta.getAttribute('aria-busy') === 'true' || cta.classList.contains('is-added')) continue;
      cta.removeAttribute('aria-disabled');
      cta.classList.toggle('is-waiting', !full);
      cta.classList.toggle('is-ready', full);
      this.#text(cta, '[data-sd-pb-cta-text]', label);
    }

    // Ticket
    this.#text(this, '[data-sd-pb-ticket-title]', `Your ${size.name}`);
    const lines = this.querySelector('[data-sd-pb-lines]');
    if (lines) {
      const html = Array.from({ length: s.size }, (_, i) => {
        const seat = s.seats[i];
        const p = seat && this.#byHandle.get(seat.h);
        const n = String(i + 1).padStart(2, '0');
        if (!p) return `<li class="sd-pb-line is-empty"><span class="sd-pb-line__n">${n}</span><span class="sd-pb-line__name">Empty seat</span></li>`;
        const name = this.#style(p, seat.v);
        const style = name ? `<span class="sd-pb-line__style">${esc(name)}</span>` : '';
        return `<li class="sd-pb-line"><span class="sd-pb-line__n">${n}</span><span class="sd-pb-line__name">${esc(p.t)}</span>${style}</li>`;
      }).join('');
      if (lines.innerHTML !== html) lines.innerHTML = html;
    }
    this.#text(this, '[data-sd-pb-total]', formatMoney(total));
    this.#text(this, '[data-sd-pb-each-value]', formatMoney(size.each));
    this.#text(this, '[data-sd-pb-sep-value]', formatMoney(this.#separately(s.size)));
    const miniRow = this.querySelector('[data-sd-pb-mini-row]');
    if (miniRow instanceof HTMLElement) miniRow.hidden = !this.#miniOn();
    const share = this.querySelector('[data-sd-pb-share]');
    // A link is only sent for a full plate: a part-filled one would open at single prices.
    if (share instanceof HTMLButtonElement) share.disabled = filled < s.size;
  }

  /* ------------------------------------------------------------------ */
  /* Feedback                                                            */
  /* ------------------------------------------------------------------ */

  /** One live region (the toast) owns every builder announcement. */
  #say(message, { undo = false } = {}) {
    const toast = this.querySelector('[data-sd-pb-status]');
    if (!(toast instanceof HTMLElement)) return;
    clearTimeout(this.#toastTimer);
    toast.innerHTML = `<span class="sd-pb-toast__msg">${esc(message)}</span>${
      undo && this.#undo ? `<button type="button" class="sd-pb-toast__undo" data-sd-pb-undo>${esc(this.#undoLabel)}</button>` : ''
    }`;
    toast.classList.add('is-visible');
    this.#toastTimer = window.setTimeout(() => {
      toast.classList.remove('is-visible');
      this.#toastTimer = window.setTimeout(() => {
        if (!toast.classList.contains('is-visible')) toast.textContent = '';
      }, 400);
    }, undo ? 7000 : 3800);
  }

  #pop(el) {
    el.classList.remove('is-pop');
    void el.offsetWidth;
    el.classList.add('is-pop');
    el.addEventListener('animationend', () => el.classList.remove('is-pop'), { once: true });
  }

  #nudge(el) {
    if (reduceMotion()) return;
    el.classList.remove('is-nudge');
    void el.offsetWidth;
    el.classList.add('is-nudge');
    el.addEventListener('animationend', () => el.classList.remove('is-nudge'), { once: true });
  }

  #celebrate() {
    const plate = this.querySelector('[data-sd-pb-plate]');
    if (plate instanceof HTMLElement && !reduceMotion()) this.#pop(plate);
    for (const cta of this.querySelectorAll('[data-sd-pb-cta]')) squish(/** @type {HTMLElement} */ (cta));
  }

  /** Hide the fixed bar only while another "Add to bag" button (plate card, order ticket, product-page banner) is
   *  actually on screen, so one is always within reach. */
  #watchTicket() {
    const bar = this.querySelector('[data-sd-pb-bar]');
    if (!(bar instanceof HTMLElement) || !('IntersectionObserver' in window)) return;
    const watched = [
      this.querySelector('[data-sd-pb-stage-cta]'),
      this.querySelector('[data-sd-pb-ticket] [data-sd-pb-cta]'),
      this.querySelector('[data-sd-pb-pre-done]'),
    ].filter((el) => el instanceof HTMLElement);
    if (!watched.length) return;
    const seen = new Map();
    this.#io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          // Mostly on screen counts; a hidden banner (display none) never intersects.
          seen.set(entry.target, entry.isIntersecting && entry.intersectionRatio > 0.6);
        }
        const hide = [...seen.values()].some(Boolean);
        bar.classList.toggle('is-hidden', hide);
        bar.inert = hide;
        this.classList.toggle('is-bar-hidden', hide);
      },
      { threshold: [0, 0.6, 1] }
    );
    for (const el of watched) this.#io.observe(el);
  }
}

if (!customElements.get('sd-plate-builder')) customElements.define('sd-plate-builder', SdPlateBuilder);
